import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  countRoutesBetween,
  groupPorts,
  hasNamedGroups,
  resolveRoute,
  toggleGroup,
  type PortGroup,
  type RouteState,
  type RouterPort,
} from "./router-model";
import {
  PortLabelText,
  PortMenu,
  RenamableLabel,
  RenameInput,
  portTitle,
  useRename,
  useStartRenameOnDoubleClick,
} from "./rename";
import { ROUTE_STATE_FILL, ROUTE_STATE_LABEL } from "./route-style";
import type { RouterViewProps } from "./types";

/* ─────────────────────────────────────────────────────────── */
/* Matrix View — sources as columns, targets as rows            */
/* ─────────────────────────────────────────────────────────── */

type Column =
  | { kind: "port"; key: string; port: RouterPort }
  | { kind: "group"; key: string; group: PortGroup; sourceIds: Set<string> };

interface Hover {
  row: string | null;
  col: string | null;
}

const NO_HOVER: Hover = { row: null, col: null };

export const RouterMatrix = ({
  sources,
  targets,
  routes,
  routeState,
  routeFallbackLabel,
  sourcesById,
  sourceNoun,
  targetNoun,
  canRouteTarget,
  clickSource,
}: RouterViewProps) => {
  const sourceGroups = useMemo(() => groupPorts(sources), [sources]);
  const targetGroups = useMemo(() => groupPorts(targets), [targets]);
  const showSourceGroups = hasNamedGroups(sourceGroups);
  const showTargetGroups = hasNamedGroups(targetGroups);

  const [collapsedSources, setCollapsedSources] = useState<Set<string>>(
    () => new Set(),
  );
  const [collapsedTargets, setCollapsedTargets] = useState<Set<string>>(
    () => new Set(),
  );
  const [hover, setHover] = useState<Hover>(NO_HOVER);

  const onHover = useCallback(
    (row: string | null, col: string | null) =>
      setHover((h) => (h.row === row && h.col === col ? h : { row, col })),
    [],
  );

  const columns = useMemo<Column[]>(
    () =>
      sourceGroups.flatMap((g): Column[] =>
        showSourceGroups && collapsedSources.has(g.key)
          ? [
              {
                kind: "group",
                key: `g:${g.key}`,
                group: g,
                sourceIds: new Set(g.ports.map((p) => p.id)),
              },
            ]
          : g.ports.map((port) => ({
              kind: "port",
              key: `s:${port.id}`,
              port,
            })),
      ),
    [sourceGroups, showSourceGroups, collapsedSources],
  );

  const toggleSourceGroup = (key: string) =>
    setCollapsedSources((prev) => toggleGroup(prev, key));
  const toggleTargetGroup = (key: string) =>
    setCollapsedTargets((prev) => toggleGroup(prev, key));

  if (sources.length === 0 || targets.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
        No {sources.length === 0 ? sourceNoun : targetNoun} ports available.
      </p>
    );
  }

  const columnHeaderTop = showSourceGroups ? "top-6" : "top-0";
  const corner = (
    <div className="flex h-full flex-col justify-end gap-0.5 text-[10px] font-normal text-muted-foreground">
      <span>{sourceNoun}s →</span>
      <span>{targetNoun}s ↓</span>
    </div>
  );

  return (
    <div
      className="max-h-[70vh] overflow-auto rounded-md border"
      onMouseLeave={() => setHover(NO_HOVER)}
    >
      <table className="border-separate border-spacing-0 text-xs">
        <thead>
          {showSourceGroups && (
            <tr>
              <th
                rowSpan={2}
                className="sticky left-0 top-0 z-30 border-b border-r bg-background p-2 text-left align-bottom"
              >
                {corner}
              </th>
              {sourceGroups.map((g) => {
                const collapsed = collapsedSources.has(g.key);
                return (
                  <th
                    key={g.key}
                    colSpan={collapsed ? 1 : g.ports.length}
                    className="sticky top-0 z-20 h-6 max-w-0 border-b border-l bg-muted p-0 text-left font-medium"
                  >
                    <button
                      type="button"
                      className="flex h-6 w-full min-w-0 items-center gap-0.5 px-1 hover:bg-accent"
                      onClick={() => toggleSourceGroup(g.key)}
                      title={`${g.label || "Ungrouped"} (click to ${collapsed ? "expand" : "collapse"})`}
                      data-testid={`router-source-group-${g.key}`}
                    >
                      {collapsed ? (
                        <ChevronRight className="h-3 w-3 shrink-0" />
                      ) : (
                        <ChevronDown className="h-3 w-3 shrink-0" />
                      )}
                      {!collapsed && (
                        <span className="truncate">{g.label || "—"}</span>
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          )}
          <tr>
            {!showSourceGroups && (
              <th className="sticky left-0 top-0 z-30 border-b border-r bg-background p-2 text-left align-bottom">
                {corner}
              </th>
            )}
            {columns.map((col) =>
              col.kind === "port" ? (
                <SourceHeader
                  key={col.key}
                  port={col.port}
                  highlighted={hover.col === col.key}
                  topClass={columnHeaderTop}
                />
              ) : (
                <th
                  key={col.key}
                  className={cn(
                    "sticky z-20 border-b border-l bg-muted p-0 align-bottom",
                    columnHeaderTop,
                    hover.col === col.key && "bg-accent",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleSourceGroup(col.group.key)}
                    className="flex h-28 w-6 items-end justify-center pb-1 text-muted-foreground hover:text-foreground"
                    title={`Expand ${col.group.label}`}
                  >
                    <span className="max-h-[6.5rem] rotate-180 truncate italic [writing-mode:vertical-rl]">
                      {col.group.label} ({col.group.ports.length})
                    </span>
                  </button>
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {targetGroups.map((g) => {
            const collapsed = showTargetGroups && collapsedTargets.has(g.key);
            const targetIds = g.ports.map((p) => p.id);
            return (
              <Fragment key={g.key}>
                {showTargetGroups && (
                  <tr>
                    <th className="sticky left-0 z-10 h-6 border-b border-r bg-muted p-0 text-left font-medium">
                      <button
                        type="button"
                        className="flex h-6 w-full min-w-0 items-center gap-0.5 px-1 hover:bg-accent"
                        onClick={() => toggleTargetGroup(g.key)}
                        data-testid={`router-target-group-${g.key}`}
                      >
                        {collapsed ? (
                          <ChevronRight className="h-3 w-3 shrink-0" />
                        ) : (
                          <ChevronDown className="h-3 w-3 shrink-0" />
                        )}
                        <span className="truncate">{g.label || "—"}</span>
                        <span className="ml-auto pl-1 font-normal text-muted-foreground">
                          {g.ports.length}
                        </span>
                      </button>
                    </th>
                    {columns.map((col) => {
                      if (!collapsed) {
                        return (
                          <td
                            key={col.key}
                            className="border-b border-l bg-muted/40"
                          />
                        );
                      }
                      const sourceIds =
                        col.kind === "port"
                          ? new Set([col.port.id])
                          : col.sourceIds;
                      return (
                        <SummaryCell
                          key={col.key}
                          count={countRoutesBetween(
                            routes,
                            targetIds,
                            sourceIds,
                          )}
                          highlighted={hover.col === col.key}
                          onHover={() => onHover(null, col.key)}
                          onClick={() => {
                            toggleTargetGroup(g.key);
                            if (col.kind === "group")
                              toggleSourceGroup(col.group.key);
                          }}
                        />
                      );
                    })}
                  </tr>
                )}
                {!collapsed &&
                  g.ports.map((target) => {
                    const resolved = resolveRoute(
                      target.id,
                      routes,
                      sourcesById,
                      routeState,
                      routeFallbackLabel,
                    );
                    const rowHighlighted = hover.row === target.id;
                    const routable = canRouteTarget(target);
                    return (
                      <tr key={target.id}>
                        <th
                          className={cn(
                            "sticky left-0 z-10 h-6 min-w-[9rem] max-w-[16rem] border-b border-r bg-background px-2 py-0 text-left font-medium",
                            rowHighlighted && "bg-accent",
                          )}
                        >
                          <div className="flex min-w-0 items-center gap-1.5">
                            <RenamableLabel
                              kind="target"
                              port={target}
                              className="flex-1"
                            />
                            {!resolved.source && resolved.label && (
                              <span
                                className="max-w-[45%] shrink-0 truncate rounded-sm border border-dashed border-muted-foreground/50 px-1 text-[10px] font-normal italic text-muted-foreground"
                                title={`${ROUTE_STATE_LABEL[resolved.state ?? "unresolved"]}: ${resolved.label}`}
                              >
                                ← {resolved.label}
                              </span>
                            )}
                          </div>
                        </th>
                        {columns.map((col) => {
                          if (col.kind === "group") {
                            return (
                              <SummaryCell
                                key={col.key}
                                count={countRoutesBetween(
                                  routes,
                                  [target.id],
                                  col.sourceIds,
                                )}
                                state={resolved.state}
                                highlighted={
                                  rowHighlighted || hover.col === col.key
                                }
                                onHover={() => onHover(target.id, col.key)}
                                onClick={() => toggleSourceGroup(col.group.key)}
                              />
                            );
                          }
                          const active = resolved.sourceId === col.port.id;
                          return (
                            <Crosspoint
                              key={col.key}
                              targetId={target.id}
                              sourceId={col.port.id}
                              colKey={col.key}
                              active={active}
                              state={active ? resolved.state : undefined}
                              highlighted={
                                rowHighlighted || hover.col === col.key
                              }
                              disabled={!routable || !!col.port.disabled}
                              title={`${col.port.label} → ${target.label}`}
                              onClick={clickSource}
                              onHover={onHover}
                            />
                          );
                        })}
                      </tr>
                    );
                  })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Column header (rotated source label)                        */
/* ─────────────────────────────────────────────────────────── */

const SourceHeader = ({
  port,
  highlighted,
  topClass,
}: {
  port: RouterPort;
  highlighted: boolean;
  topClass: string;
}) => {
  const rename = useRename();
  const onDoubleClick = useStartRenameOnDoubleClick("source", port);
  const editing = rename.isEditing("source", port.id);

  return (
    <th
      className={cn(
        "sticky z-20 border-b border-l bg-background p-0 align-bottom font-medium",
        topClass,
        highlighted && "bg-accent",
      )}
    >
      <div className="relative flex h-28 w-6 items-end justify-center pb-1">
        {editing ? (
          <RenameInput
            kind="source"
            port={port}
            className="absolute bottom-1 left-0 z-40 w-40"
          />
        ) : (
          <PortMenu kind="source" port={port}>
            <span
              data-testid={`router-source-${port.id}`}
              onDoubleClick={onDoubleClick}
              title={portTitle(port)}
              className={cn(
                "max-h-[6.5rem] rotate-180 select-none truncate [writing-mode:vertical-rl]",
                port.disabled && "text-muted-foreground opacity-60",
                onDoubleClick && "cursor-text",
              )}
            >
              <PortLabelText port={port} showDefault={false} />
            </span>
          </PortMenu>
        )}
      </div>
    </th>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Cells                                                        */
/* ─────────────────────────────────────────────────────────── */

interface CrosspointProps {
  targetId: string;
  sourceId: string;
  colKey: string;
  active: boolean;
  state: RouteState | undefined;
  highlighted: boolean;
  disabled: boolean;
  title: string;
  onClick: (targetId: string, sourceId: string) => void;
  onHover: (row: string | null, col: string | null) => void;
}

const Crosspoint = memo(
  ({
    targetId,
    sourceId,
    colKey,
    active,
    state,
    highlighted,
    disabled,
    title,
    onClick,
    onHover,
  }: CrosspointProps) => (
    <td
      className={cn(
        "h-6 w-6 min-w-[1.5rem] border-b border-l p-0",
        highlighted && "bg-accent/60",
      )}
      onMouseEnter={() => onHover(targetId, colKey)}
    >
      <button
        type="button"
        data-testid={`router-crosspoint-${targetId}-${sourceId}`}
        data-active={active || undefined}
        data-state={state}
        aria-pressed={active}
        aria-label={title}
        title={
          active && state && state !== "ok"
            ? `${title} (${ROUTE_STATE_LABEL[state]})`
            : title
        }
        disabled={disabled}
        onClick={() => onClick(targetId, sourceId)}
        className={cn(
          "group flex h-6 w-6 items-center justify-center",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
        )}
      >
        <span
          className={cn(
            "block h-3.5 w-3.5 rounded-sm border transition-colors",
            active
              ? ROUTE_STATE_FILL[state ?? "ok"]
              : cn(
                  "border-muted-foreground/20",
                  !disabled && "group-hover:border-primary/60",
                ),
            disabled && !active && "opacity-40",
          )}
        />
      </button>
    </td>
  ),
);
Crosspoint.displayName = "Crosspoint";

const SummaryCell = ({
  count,
  state,
  highlighted,
  onHover,
  onClick,
}: {
  count: number;
  state?: RouteState;
  highlighted: boolean;
  onHover: () => void;
  onClick: () => void;
}) => (
  <td
    className={cn(
      "h-6 w-6 min-w-[1.5rem] border-b border-l bg-muted/40 p-0",
      highlighted && "bg-accent/60",
    )}
    onMouseEnter={onHover}
  >
    <button
      type="button"
      onClick={onClick}
      title={count > 0 ? `${count} route${count === 1 ? "" : "s"}` : "Expand"}
      className="flex h-6 w-6 items-center justify-center"
    >
      {count > 0 && (
        <span
          className={cn(
            "block h-2 w-2 rounded-full border",
            ROUTE_STATE_FILL[state && count === 1 ? state : "ok"],
          )}
        />
      )}
    </button>
  </td>
);
